import { useState, useEffect, useCallback, useRef, Dispatch, SetStateAction } from "react";

interface UseApiQueryOptions<T> {
  deps?: unknown[];
  enabled?: boolean;
  refetchOnMount?: boolean;
  onSuccess?: ((data: T) => void) | null;
  onError?: ((error: Error) => void) | null;
  initialData?: T | null;
}

interface UseApiQueryReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<T | undefined>;
  isFirstLoad: boolean;
  setData: Dispatch<SetStateAction<T | null>>;
}

export const useApiQuery = <T = unknown>(
  queryFn: () => Promise<T>,
  options: UseApiQueryOptions<T> = {},
): UseApiQueryReturn<T> => {
  const {
    deps = [],
    enabled = true,
    refetchOnMount = true,
    onSuccess = null,
    onError = null,
    initialData = null,
  } = options;

  const [data, setData] = useState<T | null>(initialData);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const refetch = useCallback(async (): Promise<T | undefined> => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await queryFnRef.current();
      setData(result);
      setIsFirstLoad(false);

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      const errorMessage = (err as Error).message || "An error occurred";
      setError(errorMessage);

      if (onError) {
        onError(err as Error);
      }

      throw err;
    } finally {
      setLoading(false);
    }
  }, [enabled, onSuccess, onError]);

  useEffect(() => {
    if (!enabled || (!refetchOnMount && isFirstLoad)) return;

    const controller = new AbortController();

    const doFetch = async () => {
      setLoading(true);
      setError(null);

      // Race the query against the abort signal so that when the component
      // unmounts (controller.abort()), the async function completes immediately
      // rather than hanging on a pending promise (which would keep Jest's event
      // loop alive and cause CI timeouts).
      const abortPromise = new Promise<never>((_, reject) => {
        controller.signal.addEventListener("abort", () =>
          reject(new DOMException("Aborted", "AbortError")),
        );
      });

      try {
        const result = await Promise.race([queryFnRef.current(), abortPromise]);
        if (controller.signal.aborted) return;
        setData(result);
        setIsFirstLoad(false);

        if (onSuccess) {
          onSuccess(result);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        if ((err as Error).name === "AbortError") return;
        const errorMessage = (err as Error).message || "An error occurred";
        setError(errorMessage);

        if (onError) {
          onError(err as Error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return {
    data,
    loading,
    error,
    refetch,
    isFirstLoad,
    setData,
  };
};

interface UseApiMutationOptions<T> {
  onSuccess?: ((data: T) => void) | null;
  onError?: ((error: Error) => void) | null;
}

interface UseApiMutationReturn<T> {
  mutate: (...args: unknown[]) => Promise<T>;
  loading: boolean;
  error: string | null;
  data: T | null;
  reset: () => void;
}

export const useApiMutation = <T = unknown>(
  mutationFn: (...args: unknown[]) => Promise<T>,
  options: UseApiMutationOptions<T> = {},
): UseApiMutationReturn<T> => {
  const { onSuccess = null, onError = null } = options;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const mutationFnRef = useRef(mutationFn);
  mutationFnRef.current = mutationFn;

  const mutate = useCallback(
    async (...args: unknown[]): Promise<T> => {
      setLoading(true);
      setError(null);

      try {
        const result = await mutationFnRef.current(...args);
        setData(result);

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } catch (err) {
        const errorMessage = (err as Error).message || "An error occurred";
        setError(errorMessage);

        if (onError) {
          onError(err as Error);
        }

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, onError],
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    mutate,
    loading,
    error,
    data,
    reset,
  };
};

interface PaginationParams {
  offset: number;
  limit: number;
}

interface PaginatedResult {
  rows?: unknown[];
  data?: unknown[];
  totalRows?: number;
  total?: number;
  hasMore?: boolean;
}

interface UsePaginatedQueryOptions {
  pageSize?: number;
  deps?: unknown[];
}

interface UsePaginatedQueryReturn<T> {
  data: T[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  loadMore: () => void;
  refetch: () => Promise<unknown>;
}

export const usePaginatedQuery = <T = unknown>(
  queryFn: (params: PaginationParams) => Promise<PaginatedResult>,
  options: UsePaginatedQueryOptions = {},
): UsePaginatedQueryReturn<T> => {
  const { pageSize = 20, deps = [] } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);

  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean = false) => {
      const offset = pageNum * pageSize;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await queryFnRef.current({ offset, limit: pageSize });

        const newData = (result.rows || result.data || result || []) as T[];
        const total = result.totalRows || result.total || newData.length;
        const more =
          result.hasMore !== undefined
            ? result.hasMore
            : newData.length === pageSize;

        if (append) {
          setData((prev) => [...prev, ...newData]);
        } else {
          setData(newData);
        }

        setTotalCount(total);
        setHasMore(more);
        setPage(pageNum);

        return result;
      } catch (err) {
        setError((err as Error).message || "An error occurred");
        throw err;
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize],
  );

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchPage(page + 1, true);
    }
  }, [fetchPage, loadingMore, hasMore, page]);

  const refetch = useCallback(() => {
    setData([]);
    return fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    fetchPage(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    data,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    page,
    pageSize,
    loadMore,
    refetch,
  };
};

interface UsePollingOptions {
  enabled?: boolean;
}

interface UsePollingReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  refetch: () => Promise<T | undefined>;
}

export const usePolling = <T = unknown>(
  queryFn: () => Promise<T>,
  interval: number = 30000,
  options: UsePollingOptions = {},
): UsePollingReturn<T> => {
  const { enabled = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(enabled);

  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  const fetchData = useCallback(async (): Promise<T | undefined> => {
    try {
      const result = await queryFnRef.current();
      setData(result);
      setError(null);
      return result;
    } catch (err) {
      setError((err as Error).message || "An error occurred");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled, fetchData]);

  useEffect(() => {
    if (!isPolling || !enabled) return;

    let pollInterval: ReturnType<typeof setInterval> | null = setInterval(fetchData, interval);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden — stop polling
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      } else {
        // Tab became visible — fetch immediately and resume polling
        fetchData();
        if (!pollInterval) {
          pollInterval = setInterval(fetchData, interval);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPolling, enabled, interval, fetchData]);

  const startPolling = useCallback(() => setIsPolling(true), []);
  const stopPolling = useCallback(() => setIsPolling(false), []);
  const refetch = fetchData;

  return {
    data,
    loading,
    error,
    isPolling,
    startPolling,
    stopPolling,
    refetch,
  };
};

export default useApiQuery;
